import { BatchedMesh, InstancedMesh, Matrix4, type Object3D } from 'three';
import type { Vec3 } from '../geo/types';
import type { RoofBird } from '../world/life/roofBirds';
import { HEARTH_PUFFS, type Hearth } from '../world/life/hearthSmoke';
import type { BirdParts } from '../world/life/birdBatch';

export interface RooftopDiagnostics {
  readonly birds: readonly { readonly perch: Vec3; readonly at: Vec3; readonly fold: number }[];
  readonly hearths: readonly {
    readonly at: Vec3;
    readonly firstPuff: Vec3;
    readonly opacity: number;
  }[];
}

/** Read the rendered instances as well as their planned anchors, so misaligned effects are observable. */
export function rooftopDiagnostics(root: Object3D): RooftopDiagnostics {
  const bodies = root.getObjectByName('waterbird-parts');
  const parts = bodies?.userData['birdParts'] as BirdParts | undefined;
  const smoke = root.getObjectByName('hearth-smoke');
  const matrix = new Matrix4();
  const at = (mesh: InstancedMesh | BatchedMesh, i: number): Vec3 => {
    mesh.getMatrixAt(i, matrix);
    return [matrix.elements[12], matrix.elements[13], matrix.elements[14]];
  };
  const plans = bodies?.userData['roofBirds'] as readonly RoofBird[] | undefined;
  const birds =
    bodies instanceof BatchedMesh && parts && plans
      ? plans.map((bird, i) => {
          const position = at(bodies, parts.bodies[parts.bodies.length - plans.length + i]!);
          bodies.getMatrixAt(parts.feet[i]!, matrix);
          const scale = Math.hypot(matrix.elements[0], matrix.elements[1], matrix.elements[2]);
          return {
            perch: [...bird.perch] as Vec3,
            at: position,
            fold: Math.max(0, Math.min(1, (scale / bird.scale - 0.001) / 0.999)),
          };
        })
      : [];
  const hearths = smoke?.userData['hearths'] as readonly Hearth[] | undefined;
  const opacity =
    smoke instanceof InstancedMesh ? smoke.geometry.getAttribute('aSmokeOpacity') : null;
  return {
    birds,
    hearths:
      smoke instanceof InstancedMesh && hearths && opacity
        ? hearths.map((hearth, i) => ({
            at: [...hearth.at] as Vec3,
            firstPuff: at(smoke, i * HEARTH_PUFFS),
            opacity: Math.max(
              ...Array.from({ length: HEARTH_PUFFS }, (_, p) => opacity.getX(i * HEARTH_PUFFS + p))
            ),
          }))
        : [],
  };
}
